<template>
  <div>
    <popup-hint hint="scheduling">
      <template v-slot:breadcrumbs>
        <breadcrumbs home="Scheduling">
          <template v-slot:right>
            <v-menu offset-y v-if="company.permissions.includes('scheduling.write')" :close-on-content-click="false" nudge-bottom="5" left>
              <template v-slot:activator="{ on }">
                <v-icon small v-on="on">mdi-tune-variant</v-icon>
              </template>
              <planning-scheduling-preferences @update="preferences=$event;getPeriod()"/>
            </v-menu>
          </template>
        </breadcrumbs>
      </template>
      <template v-slot:top>
        You can schedule your production activities, managing
        actions, people, equipment and inventory.
      </template>
    </popup-hint>

    <v-list-item>
      <filter-menu include-reset @reset="filters.siteId=null;filters.type='';filters.search='';changeDate()">
        <v-list-item>
          <filter-search label="Search" v-model="filters.search"/>
        </v-list-item>
        <v-list-item>
          <date-picker label="Starting" v-model="filters.dateStart" date-only @change="changeDate()"/>
        </v-list-item>
        <v-list-item>
          <v-select label="Activity type" v-model="filters.type" :items="types" outlined dense/>
        </v-list-item>
        <v-list-item v-if="sites.length>1">
          <v-autocomplete label="Site" dense outlined :items="[{id:null,name:'All'},{divider:true},...sites]" v-model="filters.siteId" item-text="name" item-value="id"  @change="changeDate()"/>
        </v-list-item>
      </filter-menu>

      <v-btn-toggle v-model="filters.show" color="primary" mandatory @change="filters.search = ''" class="mr-2">
        <v-tooltip bottom>
          <template v-slot:activator="{on}">
            <v-btn value="Activity" small v-on="on"><v-icon small>mdi-cogs</v-icon></v-btn>
          </template>
          Activities
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{on}">
            <v-btn value="Equipment" small v-on="on"><v-icon small>mdi-tools</v-icon></v-btn>
          </template>
          Equipment
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{on}">
            <v-btn value="Inventory" small v-on="on"><v-icon small>mdi-tag</v-icon></v-btn>
          </template>
          Inventory
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{on}">
            <v-btn value="Roles" small v-on="on"><v-icon small>mdi-account-multiple</v-icon></v-btn>
          </template>
          Roles
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{on}">
            <v-btn value="People" small v-on="on"><v-icon small>mdi-account</v-icon></v-btn>
          </template>
          People
        </v-tooltip>
      </v-btn-toggle>

      <v-chip v-show="filters.siteId!=null" close small class="ml-1" @click:close="filters.siteId=null;changeDate()">{{siteName}}</v-chip>
      <v-chip v-show="filters.type!=''" close small class="ml-1" @click:close="filters.type=''">{{types.find(type => type.value === filters.type).text}}</v-chip>

      <v-spacer/>
      <v-menu offset-y nudge-width="200" eager>
        <template v-slot:activator="{ on }">
          <v-btn small icon v-on="on" v-on:click.prevent><v-icon>mdi-cog-outline</v-icon></v-btn>
        </template>
        <v-list dense>
          <v-list-item :to="{name:'PlanningSchedulingTemplates'}"><v-list-item-title>Templates</v-list-item-title></v-list-item>
          <v-list-item :to="{name:'PlanningSchedulingRoles'}"><v-list-item-title>Roles</v-list-item-title></v-list-item>
        </v-list>
      </v-menu>
    </v-list-item>

    <v-overlay :value="loading" absolute opacity="0">
      <v-progress-circular indeterminate size="150" color="primary"/>
    </v-overlay>

    <v-data-table ref="table" class="purtrak-table schedule" :items="listItems" no-data-text="" v-show="view==='timeline'"
                  disable-sort hide-default-header fixedHeader disable-pagination height='calc(100vh - 153px)'
                  hide-default-footer>

      <!-- Header -->
      <template v-slot:header>
        <thead>
          <tr>
            <th width="2.5%"/>
            <th width="20%" class="text-right">
              <v-select :items="['Daily','Weekly','Monthly']" v-model="filters.interval" dense style="width:100px" hide-details class="d-inline-block mr-2 text-caption" @change="resetDate()"/>
              <v-icon @click="back()">mdi-arrow-left-circle</v-icon>
              <v-icon @click="forward()">mdi-arrow-right-circle</v-icon>
            </th>
            <template v-if="filters.interval==='Daily'">
              <th :colspan="firstMonthColumnsLength" class="pl-2">
                <template v-if="firstMonthColumnsLength>=4 && periods[0]">
                  {{periods[0].start | formatDate('MMMM yyyy')}}
                </template>
              </th>
              <th v-if="periods.length-firstMonthColumnsLength>0" :colspan="periods.length-firstMonthColumnsLength" class="pl-2">
                {{secondMonth}}
              </th>
            </template>
            <th v-else colspan="12"/>
          </tr>
          <tr>
            <th/>
            <th>{{filters.show | uppercase}}
              <v-btn v-if="filters.show==='Activity'" fab color="primary" small style="position:absolute;right:6px;top:4px" @click="activityAdd()"><v-icon @click="activityAdd()">mdi-plus</v-icon></v-btn>
            </th>
            <template v-for="period in periods">
              <th class="px-0 text-center" :width="filters.interval==='Daily'?'2.5%':'5%'"
                  :class="{'week-start':filters.interval==='Daily' && weekStart(period),
                            workDay:filters.interval==='Daily' && (!workDay(period.start) && period.start!==today),
                            dark:darkMode,
                            primary:filters.interval==='Daily' && period.start===today,
                            'white--text':filters.interval==='Daily' && period.start===today}"
                  @dblclick="filters.interval==='Daily' ? holiday(period.start) : ''">
                <v-tooltip bottom v-if="filters.interval==='Daily' && holidayName(period)">
                  <template v-slot:activator="{on}">
                    <div v-on="on">{{period.title}}</div>
                  </template>
                  {{holidayName(period)}}
                </v-tooltip>
                <template v-else>
                  {{period.title}} <!-- i.e. day of month -->
                </template>
                <div class="text-caption" v-if="filters.interval==='Daily'">{{period.start | formatDate('EEEEEE')}}</div>    <!-- i.e. Weekday -->
                <div class="text-caption" v-else>{{period.start | formatDate('yyyy')}}</div>
              </th>
            </template>
          </tr>
        </thead>
      </template>

      <!-- Activities -->
      <template v-if="filters.show==='Activity'" v-slot:item="{item}">
        <tr>
          <td class="px-2"><v-icon @click="toggleExpandActivity(item)" v-if="item.hasSubActivities">{{item.expanded?'mdi-chevron-up':'mdi-chevron-down'}}</v-icon></td>
          <td :style="{textIndent:20*item.indent+'px'}" style="position:relative">
            <div>
              {{item.description}}
            </div>
            <v-btn  v-if="item.hasSubActivities" fab color="primary" x-small style="position:absolute;right:10px;top:8px" @click="activityAdd(item.startDate, item)"><v-icon small>mdi-plus</v-icon></v-btn>
            <div class="text-caption" :class="activityClass(item, true)">
              <template v-if="item.completed">Completed {{item.completed | formatDateTime}}</template>
              <template v-else-if="!item.hasSubActivities && !item.roleId && item.assignTo.length === 0">No assignee</template>
              <template v-else-if="item.outstandingDependents">Waiting</template>
              <template v-else-if="!item.hasSubActivities && item.startDate < today">Overdue</template>
              <template v-else-if="!item.hasSubActivities && item.startDate === today">Now due</template>
            </div>
          </td>
          <template v-for="period in periods">
            <td :key="period.start" class="px-0"
                :class="{'week-start':filters.interval==='Daily' && weekStart(period),
                         dark:darkMode,
                         workDay:filters.interval==='Daily' && !workDay(period.start)}"
                         @dblclick="activityAdd(period.start)">
              <template v-if="item.startDate <= period.end && item.endDate >= period.start && workDay(period.start)">
                <v-menu :nudge-width="150" offset-y :disabled="readOnly">
                  <template v-slot:activator="{ on }">
                    <div style="position:relative" v-on="on" :class="activityClass(item, false)" @dblclick.stop="" :ref="item.id">
                      <template v-if="item.dependsOn.length && item.startDate === period.start && showConnections">
                        <svg v-for="ref of item.dependsOn" :key="ref"
                              style="position:absolute; pointer-events: none" :style="getConnectionSVGBox(item.id, ref)">
                          <polyline stroke="blue" fill="none" :points="getConnectionLinePoints(item.id, ref)"/>
                          <polyline stroke="blue" fill="blue" :points="getConnectionLineArrow(item.id, ref)"/>
                        </svg>
                      </template>
                      {{'\xa0'}}
                    </div>
                  </template>
                  <v-list dense>
                    <template v-if="!item.hasSubActivities && !!item.primitiveType">
                      <v-list-item @click="printWorkOrder(item)"><v-list-item-title>Print work order</v-list-item-title></v-list-item>
                      <v-divider/>
                    </template>
                    <v-list-item @click="activity=item;action='Edit';toTemplate=false;drawer=true"><v-list-item-title>Edit activity</v-list-item-title></v-list-item>
                    <v-list-item @click="activity=item;action='Copy';toTemplate=false;drawer=true"><v-list-item-title>Copy activity</v-list-item-title></v-list-item>
                    <v-list-item @click="activity=item;action='Copy';toTemplate=true;drawer=true"><v-list-item-title>Copy to template</v-list-item-title></v-list-item>
                    <v-list-item v-if="item.hasSubActivities" @click="activityAdd(period.start,item)"><v-list-item-title>Add subactiviy</v-list-item-title></v-list-item>
                    <v-divider/>
                    <v-list-item @click="activity=item;showDelete=true"><v-list-item-title>Delete activity</v-list-item-title></v-list-item>
                    <template v-if="!item.hasSubActivities">
                      <v-divider/>
                      <v-list-item v-if="!item.completed" @click="markAsComplete(item)"><v-list-item-title>Mark as complete</v-list-item-title></v-list-item>
                      <v-list-item v-else @click="markAsIncomplete(item)"><v-list-item-title>Mark as incomplete</v-list-item-title></v-list-item>
                    </template>
                  </v-list>
                </v-menu>
              </template>
            </td>
          </template>
        </tr>
      </template>

      <!-- Equipment, Roles and People -->
      <template v-else-if="filters.show==='Equipment' || filters.show==='Roles' || filters.show==='People'" v-slot:item="{item}">
        <tr>
          <td class="px-2"></td>
          <td>
            <div>
              {{item.name}}
            </div>
          </td>
          <template v-for="period in periods">
            <td :key="period.start" class="px-0"
                :class="{'week-start':filters.interval==='Daily' && weekStart(period),
                         dark:darkMode,
                         workDay:filters.interval==='Daily' && !workDay(period.start)}">
              <template v-if="!!resourceInPeriod(item, period)">
                <v-tooltip bottom open-delay="500">
                  <template v-slot:activator="{ on }">
                    <div style="position:relative" v-on="on"
                        :class="filters.show==='Equipment' && equipmentConflicts(item, period) ? 'red' : 'green'" @dblclick.stop=""
                        :ref="item.id">{{'\xa0'}}</div>
                  </template>
                  <template v-for="activity in item.used">
                    <div v-if="period.start >= activity.startDate && period.start <= activity.endDate">
                      • {{activity.activityDescription}}
                    </div>
                  </template>
                </v-tooltip>
              </template>
            </td>
          </template>
        </tr>
      </template>

      <!-- Inventory -->
      <template v-else-if="filters.show==='Inventory'" v-slot:item="{item}">
        <tr>
          <td class="px-2"></td>
          <td>
            <div>
              {{item.name}}
            </div>
          </td>
          <template v-for="period in periods">
            <td :key="period.start" class="px-0"
                :class="{'week-start':filters.interval==='Daily' && weekStart(period),
                         dark:darkMode,
                         workDay:filters.interval==='Daily' && !workDay(period.start)}">
              <template v-for="usedResource in [resourceInPeriod(item, period)]" v-if="!!usedResource">
                <v-menu bottom open-delay="500" open-on-hover>
                  <template v-slot:activator="{ on }">
                    <div style="position:relative" v-on="on" :class="materialClass(item,usedResource)" @dblclick.stop="" :ref="item.id">{{'\xa0'}}</div>
                  </template>
                  <v-card class="pa-2 text-caption" width="400">
                    <v-container>
                      <v-row dense>
                        <v-col>{{period.start | formatDate}}</v-col>
                      </v-row>
                      <v-row dense>
                        <v-col>{{item.name}}</v-col>
                      </v-row>
                      <v-divider class="my-2"/>
                      <v-row dense>
                        <v-col cols="8"><strong>Activity</strong></v-col>
                        <v-col cols="4" align="right"><strong>Usage</strong></v-col>
                      </v-row>
                      <v-row dense v-for="activity in usedResource">
                        <v-col cols="8">{{activity.activityDescription}}</v-col>
                        <v-col cols="4" align="right">{{activity.quantity | number(item.precision)}} {{item.units}}</v-col>
                      </v-row>
                      <template v-if="item.tracked">
                        <v-divider class="my-2"/>
                        <v-row dense>
                          <v-col cols="8">On hand</v-col>
                          <v-col cols="4" align="right">{{usedResource[0].onHand | number(item.precision)}} {{item.units}}</v-col>
                        </v-row>
                        <v-row dense>
                          <v-col cols="8">On order</v-col>
                          <v-col cols="4" align="right">+ {{usedResource[0].onOrder | number(item.precision)}} {{item.units}}</v-col>
                        </v-row>
                        <v-row dense>
                          <v-col cols="8">Planned used</v-col>
                          <v-col cols="4" align="right">- {{usedResource[0].estimatedUsageToDate | number(item.precision)}} {{item.units}}</v-col>
                        </v-row>
                        <v-row dense>
                          <v-col cols="8">Planned created</v-col>
                          <v-col cols="4" align="right">+ {{usedResource[0].estimatedCreated | number(item.precision)}} {{item.units}}</v-col>
                        </v-row>
                        <v-row dense>
                          <v-col cols="8"><strong>Expected after</strong></v-col>
                          <v-col cols="4" align="right" :class="materialClass(item, usedResource)+'--text'"><strong>= {{usedResource[0].onHand+usedResource[0].onOrder-usedResource[0].estimatedUsageToDate+usedResource[0].estimatedCreated | number(item.precision)}} {{item.units}}</strong></v-col>
                        </v-row>
                        <template v-if="materialClass(item,usedResource) !== 'green'">
                          <v-divider class="my-2"/>
                          <v-row dense>
                            <v-col align="center">
                              <v-btn x-small color="warning" text :to="{name:'PurchasingPurchaseOrdersEditNew',params:{itemType:item.type,itemId:item.id}}">
                                Create Purchase Order by {{reorderBy(period.start, item) | formatDate}}
                              </v-btn>
                            </v-col>
                          </v-row>
                        </template>
                      </template>
                    </v-container>
                  </v-card>
                </v-menu>
              </template>
            </td>
          </template>
        </tr>
      </template>

      <template v-slot:body.prepend v-if="activities.length === 0 && filters.show==='Activity'">
        <tr>
          <td class="px-2"/>
          <td/>
          <template v-for="period in periods">
            <td :key="period.start" class="px-0" :class="{'week-start':filters.interval==='Daily' && weekStart(period),
                                                           dark:darkMode,
                                                           workDay:filters.interval==='Daily' && !workDay(period.start)}" @dblclick="activityAdd(period.start)"/>
          </template>
        </tr>
      </template>
    </v-data-table>

    <v-navigation-drawer v-model="drawer" right temporary absolute width="450" stateless>
      <planning-scheduling-activity :activity="activity" :action="action" :toTemplate="toTemplate" :date="date" @close="drawer=false;activity={}" @refresh="getPeriod();drawer=false;activity={}"/>
    </v-navigation-drawer>

    <delete-object v-model="showDelete" name="this activity" @delete="performDelete"/>

    <v-dialog v-model="showHoliday" persistent max-width="400">
      <v-card>
        <v-card-title>Set holiday</v-card-title>
        <v-divider/>
        <v-card-text class="pa-4">
          <v-text-field label="Description" v-model="holidayObject.description" dense outlined :rules="[fieldRules.required]"/>
        </v-card-text>
        <v-divider/>
        <v-card-actions class="pr-6">
          <v-btn color="primary" outlined @click="clearHoliday()" v-if="!holidayObject.create">Clear</v-btn>
          <v-spacer/>
          <v-btn plain color="primary" @click="showHoliday=false">Cancel</v-btn>
          <v-btn color="primary" @click="setHoliday()">Set</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<style>
.schedule .week-start {
  border-left:1px solid black !important;
}
.schedule .workDay {
  background-color:#f4f4f4 !important;
}
.schedule .workDay.dark {
  background-color:#000000 !important;
}
.schedule table td:nth-child(n+3) {
  border-left:thin solid rgba(0, 0, 0, 0.12);
}
.schedule table tr:first-child th:nth-child(n+3) {
  border-left:thin solid rgba(0, 0, 0, 0.12);
}
.schedule table tr:nth-child(2) th:nth-child(n+3) {
  border-left:thin solid rgba(0, 0, 0, 0.12);
}
.schedule table tr:hover {
  background-color: transparent !important;
}
.schedule .group {
  border-top: 2px dotted black !important;
  border-bottom: 2px dotted black !important;
}
</style>

<script>
import {format, add, parse, lastDayOfMonth, subBusinessDays, isSaturday, isSunday, startOfWeek} from 'date-fns'
import PlanningSchedulingActivity from '@/views/planning/scheduling/PlanningSchedulingActivity'
import PlanningSchedulingPreferences from '@/views/planning/scheduling/PlanningSchedulingPreferences'
import productionBlendingPrintBlend from '@/views/production/blending/productionBlendingPrintBlend'
import productionBottlingPrintBottling from '@/views/production/bottling/productionBottlingPrintBottling'
import productionDistillingPrintDistillation from '@/views/production/distilling/productionDistillingPrintDistillation'
import productionMashingPrintMash from '@/views/production/mashing/productionMashingPrintMash'

export default {
  name: 'PlanningScheduling',
  mixins: [productionBlendingPrintBlend,productionBottlingPrintBottling,productionDistillingPrintDistillation,productionMashingPrintMash],
  components: {PlanningSchedulingActivity,PlanningSchedulingPreferences},
  data() {
    return {
      drawer: false,
      action: '',
      toTemplate: false,
      preferences: {},
      filters: {
        dateStart: '',
        dateEnd: '',
        interval: 'Daily',
        siteId: null,
        type: '',
        search: '',
        show: 'Activity'
      },
      types: [{text:'All',value:''},{divider:true},{text:'Mash',value:'MashBill'},{text:'Distill',value:'Process'},
              {text:'Blend',value:'Formula'},{text:'Bottle',value:'Product'},{text:'Assemble',value:'Item'},
              {divider:true},{text:'Ad-hoc',value:'adhoc'},{text:'Group',value:'group'}],
      today: format(new Date(), 'yyyy-MM-dd'),
      view: 'timeline',
      showConnections: false,
      loading: false,

// Primative                                    use
// description                                  define, use
// Start                                        use
// Color                                        define, use
// duration: time                               define, use
// equpiment ->  e.g. tank1                     use (define?)
// People -> e.g. Barry or Distillers group)    use
// Materials                                    use ()
// sub activities                               define, use if ad hoc
// dependent on                                 use, define within template


// Activities:
/*
Mashing
Distilling
Blending
Bottling
Assembly
Tank - proof, fill cask, move to bc, move to tank, transfer to user, transfer to spirits, reduce inventory, denature, adjust, maintenance
fermenter - reduce inventory, move to fermenter, maintenance,
still - mainteance
other - maintenance
products- move to duty-paid, return to tank, convert, reduce, adjust, reassign lot
bc -
cask -
items
bundles
shipment
receipt
*/

      holidays: [],
      activities: [],
      activityList: null,
      date: '',
      activity: {},
      equipment: null,
      materials: null,
      roles: null,
      people: null,
      showDelete: false,
      showHoliday: false,
      holidayObject: {},
//      calendarData: []
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    readOnly() {
      return !this.company.permissions.includes('scheduling.write');
    },
    sites() {
      return this.$store.getters.sites();
    },
    siteName() {
      return this.sites.find(site => site.id === this.filters.siteId)?.name;
    },
    list() {
      return {Activity:this.activityList,Equipment:this.equipment,Inventory:this.materials,Roles:this.roles,People:this.people}[this.filters.show];
    },
    listItems() {
      if (!this.list)
        return [];

      const search = this.filters.search?.toLowerCase();

      if (this.filters.show === 'Activity')
        return this.list.filter(activity => {
          if (search && !activity.description.toLowerCase().includes(search))
            return false;

          if (this.filters.type &&
              (
               this.filters.type === 'adhoc' && (activity.primitiveType !== null || activity.hasSubActivities) ||
               this.filters.type === 'group' && !activity.hasSubActivities ||
               this.filters.type !== 'adhoc' && this.filters.type !== 'group' && activity.primitiveType !== this.filters.type
              )
             )
            return false;

          return true;
        });

      else if (search)
        return this.list.filter(item => item.name.toLowerCase().includes(search));

      return this.list;
    },
    periods() {
      const periods = [];
      const refDate = new Date();
      let period = parse(this.filters.dateStart, 'yyyy-MM-dd', refDate);
      const end = parse(this.filters.dateEnd, 'yyyy-MM-dd', refDate);
      while (period.getTime() <= end.getTime()) {
/*
        if (this.preferences.hideWeekend && (!this.preferences.allowSaturday && isSaturday(period) || !this.preferences.allowSunday && isSunday(period)))
          ;
        else
        */
        if (this.showDay(period)) {
          const start = format(period, 'yyyy-MM-dd');
          const end = this.filters.interval === 'Daily'
                    ? start
                    : (this.filters.interval === 'Monthly' ? format(lastDayOfMonth(period), 'yyyy-MM-dd')
                                                           : format(add(period,{days:6}), 'yyyy-MM-dd'));
          const dateFormat = this.filters.interval === 'Daily' ? 'd' : (this.filters.interval === 'Monthly' ? 'MMM' : 'MMM d');
          periods.push({start, end, title:format(period, dateFormat)});
        }
        period = add(period, this.filters.interval === 'Monthly' ? {months: 1} : {days:this.filters.interval === 'Weekly'?7:1});
      }
      return periods;
    },
    firstMonthColumnsLength() {
      let date = new Date(`${this.filters.dateStart}T00:00:00`);
      let lastDay = lastDayOfMonth(date);
      while (!this.showDay(lastDay))
        lastDay = add(lastDay, {days:-1});
      let n = 1;
      while (date < lastDay) {
        if (this.showDay(date))
          n++;
        date = add(date, {days:1});
      }
      return n;
    },
    secondMonth() {
      if (this.periods.length - this.firstMonthColumnsLength > 3)
        return format(add(new Date(`${this.filters.dateStart}T00:00:00`), {months:1}), 'MMMM yyyy');
      return '';
    },
    mainMenu() {
      return this.$store.state.drawer;
    }
  },

  watch: {
    'filters.show'() {
      if (!this.list) {
        const func = {Activity:this.getActivities,Equipment:this.getEquipment,Inventory:this.getMaterials,Roles:this.getRoles,People:this.getPeople}[this.filters.show];
        func();
      }

      else if (this.filters.show === 'Activity') {   // Force redraw
        this.showConnections = false;
        this.$nextTick(() => this.showConnections = true);
      }
    },
    mainMenu() {
      if (this.filters.show === 'Activity') {
        this.showConnections = false;
        setTimeout(() => this.showConnections = true, 1000);
      }
    }
  },

  async created() {
    if (this.$vuetify.breakpoint.mobile) {
      this.$store.commit('setError', {type:'warning',message:`We're sorry, but scheduling is not available on mobile devices`});
      this.$router.back();
      return;
    }

    const results = await this.$apiCall(`query PlanningScheduling {
                                           schedulePreferences {
                                             allowSaturday,allowSunday,weekStart,hideWeekend
                                           }
                                          }`,
                                          {});
    if (!results.error)
      this.preferences = results.data.schedulePreferences;
  },
  async mounted() {
    this.filters.dateStart = this.$route.query.dateStart ?? localStorage.getItem('scheduleStart') ?? format(new Date(), 'yyyy-MM')+'-01';
    await this.changeDate();
  },
  methods: {
    reorderBy(period, item) {
      const date = new Date(`${period}T00:00:00`);
      return format(item.reorderBusinessDays ? subBusinessDays(date, item.reorderDays) : add(date, {days:-item.reorderDays}), 'yyyy-MM-dd');
    },
    showDay(date) {
      if (this.filters.interval === 'Daily' && this.preferences.hideWeekend && (!this.preferences.allowSaturday && isSaturday(date) || !this.preferences.allowSunday && isSunday(date)))
        return false;
      return true;
    },
    activityClass(activity, text) {
      let c = 'grey';
      if (activity.completed)
        c = 'green';

      else if (!activity.hasSubActivities && !activity.roleId && activity.assignTo.length === 0)
        c = 'warning';

      else if (activity.outstandingDependents)
        c = 'grey';

      else if (activity.startDate <= this.today)
        c = 'red';

      return `${c}${text?'--text':(activity.hasSubActivities ? ' group' : '')}`;
    },
    activitiesForDate(date) {
      if (this.workDay(date))
        return this.activities.filter(activity => date >= activity.startDate && date <= activity.endDate);
      return [];
    },
    materialClass(item,materialUsage) {
      if (item.tracked && materialUsage[0].onHand + materialUsage[0].onOrder - materialUsage[0].estimatedUsageToDate + materialUsage[0].estimatedCreated < 0)
        return 'error';

      if (item.tracked && materialUsage[0].onHand - materialUsage[0].estimatedUsageToDate + materialUsage[0].estimatedCreated < 0)
        return 'warning';

      return 'green';
    },
    holiday(date) {
      const holiday = this.holidays.find(holiday => holiday.date === date) || {date, description:'',create:true};
      this.holidayObject = {...holiday};
      this.showHoliday = true;
    },
    async setHoliday() {
      if (!this.holidayObject.description)
        return;
      this.loading = true;
      const {create,...input} = this.holidayObject;
      const results = await this.$apiCall(`mutation PlanningScheduling($input:ScheduleHolidayInput!) {
                                             scheduleSetHoliday(input:$input) {
                                               date,description
                                             }
                                           }`,
                                           {input});
      this.loading = false;
      if (!results.error) {
        this.holidays.push(results.data.scheduleSetHoliday);
        this.getPeriod();
      }
      this.showHoliday = false;
    },
    async clearHoliday() {
      this.loading = true;
      const results = await this.$apiCall(`mutation PlanningScheduling($date:Date!) {
                                             scheduleClearHoliday(date:$date)
                                           }`,
                                           {date:this.holidayObject.date});
      this.loading = false;
      if (!results.error) {
        this.holidays.splice(this.holidays.findIndex(holiday => holiday.date === this.holidayObject.date), 1);
        this.getPeriod();
      }
      this.showHoliday = false;
    },
    async toggleExpandActivity(activity) {
      activity.expanded = !activity.expanded;
      this.activityList = this.buildActivityList(this.activities, 0);
      this.$nextTick(() => {      // A hack, but needed to force DOM refresh to get proper position of elements
//        this.filters.dateEnd = format(add(new Date(`${this.filters.dateStart}T00:00:00`), {days:26}), 'yyyy-MM-dd');
        const interval = this.filters.interval === 'Daily' ? {days:27} : (this.filters.interval === 'Weekly' ? {weeks:11} : {months:11});
        this.filters.dateEnd = format(add(new Date(`${this.filters.dateStart}T00:00:00`), interval), 'yyyy-MM-dd');

      });
    },
    buildActivityList(activities, indent) {
      const list = [];
      for (const activity of activities) {
        activity.indent = indent;
        list.push(activity);
        if (activity.expanded)
          list.push(...this.buildActivityList(activity.subActivities, indent+1));
      }
      return list;
    },
    getConnectingNumbers(to, from) {
      const elFrom = this.$refs[from];
      const elTo = this.$refs[to];
      if (!elFrom || !elTo || elFrom.length === 0 || elTo.length === 0)
        return {top:0, height:0, left:0, width:0, x1:0, x2: 0, y1:0, y2:0};

      let rectFrom = {left:0,top:0,width:0,height:0};
      for (const el of elFrom) {
        const rect = el.getBoundingClientRect();
        if (rect.left > rectFrom.left)
          rectFrom = rect;
      }

      let rectTo = {left:99999};
      for (const el of elTo) {
        const rect = el.getBoundingClientRect();
        if (rect.left < rectTo.left)
          rectTo = rect;
      }

      const y = rectTo.top - rectFrom.top;
      const width = rectTo.left-rectFrom.left+rectFrom.width;
      const height = rectTo.top-rectFrom.top+rectFrom.height;
      return {top:-y, height:y+rectFrom.height, left:rectFrom.left-rectTo.left, width,
              x1:rectFrom.width/2, x2:width-rectFrom.width/2,
              y1:rectFrom.height/2, y2:height-rectFrom.height/2};
    },
    getConnectionSVGBox(to, from) {
      const numbers = this.getConnectingNumbers(to, from);
      return {top:`${numbers.top}px`,
              height:`${numbers.height}px`,
              left:`${numbers.left}px`,
              width:`${numbers.width}px`};
    },
    getConnectionLinePoints(to, from) {
      const numbers = this.getConnectingNumbers(to, from);
      return `${numbers.x1},${numbers.y1},${numbers.x1},${numbers.y2},${numbers.x2},${numbers.y2}`;
    },
    getConnectionLineArrow(to, from) {
      const numbers = this.getConnectingNumbers(to, from);
      if (numbers.x1 === numbers.x2) // Down arrow
        return `${numbers.x2-5},${numbers.y2-5},${numbers.x2+5},${numbers.y2-5},${numbers.x2},${numbers.y2}`;
      else                          // right arrow
        return `${numbers.x2-5},${numbers.y2-5},${numbers.x2-5},${numbers.y2+5},${numbers.x2},${numbers.y2}`;
    },
    activityAdd(date, parent) {
      if (parent && !parent.expanded)
        this.toggleExpandActivity(parent);
      this.date = date;
      this.action = 'Add';
      this.activity = {primitiveId:null,siteId:this.sites[0].id,description:'',startDate:this.date||format(new Date(),'yyyy-MM-dd'), duration:60,color:'#00ff00FF',quantity:0,dependsOn:[],
                       hasSubActivities:false,parentId:parent?.id,offsetDays:0,equipment:[],materials:[],assignTo:[],roleId:''};
      this.drawer = true;
    },
    weekStart(period) {
      const weekStart = this.preferences.hideWeekend ? (this.preferences.allowSunday ? this.preferences.weekStart : 'Monday') : this.preferences.weekStart;
      const date = new Date(`${period.start}T00:00:00`);
      return format(date, 'EEEE') === weekStart;
    },
    workDay(forDate) {
      if (this.filters.interval !== 'Daily')
        return true;
      const date = new Date(`${forDate}T00:00:00`);
      const formattedDate = format(date, 'yyyy-MM-dd');
      if (isSaturday(date) && !this.preferences.allowSaturday ||
          isSunday(date) && !this.preferences.allowSunday ||
          this.holidays.some(holiday => holiday.date === formattedDate))
        return false;
      return true;
    },
    holidayName(period) {
      const date = new Date(`${period.start}T00:00:00`);
      const formattedDate = format(date, 'yyyy-MM-dd');
      return this.holidays.find(holiday => holiday.date === formattedDate)?.description;
    },
    changeDate() {
      localStorage.setItem('scheduleStart', this.filters.dateStart);
//      this.filters.dateEnd = format(add(new Date(`${this.filters.dateStart}T00:00:00`), {days:27}), 'yyyy-MM-dd');
      const interval = this.filters.interval === 'Daily' ? {days:27} : (this.filters.interval === 'Weekly' ? {weeks:11} : {months:11});
      this.filters.dateEnd = format(add(new Date(`${this.filters.dateStart}T00:00:00`), interval), 'yyyy-MM-dd');
      this.getPeriod();
    },
    resetDate() {
      if (this.filters.interval === 'Monthly')
        this.filters.dateStart = `${this.filters.dateStart.substring(0,8)}01`;
      else if (this.filters.interval === 'Weekly')
        this.filters.dateStart = format(startOfWeek(new Date(`${this.filters.dateStart}T00:00:00`), {weekStartsOn:this.preferences.weekStart==='Sunday'?0:1}), 'yyyy-MM-dd');
      this.changeDate();
    },
    back() {
      let date = new Date(`${this.filters.dateStart}T00:00:00`);
      const interval = this.filters.interval === 'Daily' ? {days:-1} : (this.filters.interval === 'Weekly' ? {days:-7} : {months:-1});
      do {
        date = add(date, interval);
      } while (!this.showDay(date));
      this.filters.dateStart = format(date, 'yyyy-MM-dd');
      this.changeDate();
    },
    forward() {
      let date = new Date(`${this.filters.dateStart}T00:00:00`);
      const interval = this.filters.interval === 'Daily' ? {days:1} : (this.filters.interval === 'Weekly' ? {days:7} : {months:1});
      do {
        date = add(date, interval);
      } while (!this.showDay(date));
      this.filters.dateStart = format(date, 'yyyy-MM-dd');
      this.changeDate();
    },
    async getPeriod() {
      this.activityList = null;
      this.equipment = null;
      this.materials = null;
      this.roles = null;
      this.people = null;
      const results = await this.$apiCall(`query PlanningScheduling($from:Date!, $to:Date!) {
                                              scheduleHolidays(from:$from,to:$to) {
                                                date,description
                                              }
                                            }`,
                                            {from:this.filters.dateStart, to:this.filters.dateEnd});
      if (!results.error)
        this.holidays = results.data.scheduleHolidays;

      const func = {Activity:this.getActivities,Equipment:this.getEquipment,Inventory:this.getMaterials,Roles:this.getRoles,People:this.getPeople}[this.filters.show];
      func();
    },
    async getActivities() {
      this.loading = true;
      this.showConnections = false;
      const results = await this.$apiCall(`query PlanningScheduling($from:Date!, $to:Date!, $siteId:ID) {
                                              activities(from:$from,to:$to,siteId:$siteId) {
                                                ...activityFields
                                                subActivities {
                                                  ...activityFields
                                                }
                                              }
                                            }
                                            fragment activityFields on Activity {
                                              id,description,startDate,endDate,duration,color,quantity,primitiveId,primitiveType,siteId,siteName
                                              dependsOn,outstandingDependents,
                                              hasSubActivities,parentId,offsetDays,equipment
                                              materials {type,id,quantity}
                                              roleId,roleName,assignTo,completed
                                            }`, {from:this.filters.dateStart, to:this.filters.dateEnd,siteId:this.filters.siteId});
      if (!results.error) {
        const oldActivities = this.activities || [];
        this.activities = results.data.activities.map(activity => {
          const expanded = oldActivities.find(a => a.id === activity.id)?.expanded || false;
          return {...activity, expanded, indent:0}
        });
      }
      this.activityList = this.buildActivityList(this.activities, 0);
      this.loading = false;
      this.$nextTick(() => this.showConnections = true);
    },
    async getEquipment() {
      this.loading = true;
      const results = await this.$apiCall(`query PlanningScheduling($from:Date!, $to:Date!, $siteId:ID) {
                                              activitiesEquipment(from:$from,to:$to,siteId:$siteId) {
                                                id,name
                                                used {activityId,activityDescription,startDate,endDate}
                                              }
                                            }`, {from:this.filters.dateStart, to:this.filters.dateEnd,siteId:this.filters.siteId});
      if (!results.error)
        this.equipment = results.data.activitiesEquipment;
      this.loading = false;
    },
    async getMaterials() {
      this.loading = true;
      const results = await this.$apiCall(`query PlanningScheduling($from:Date!, $to:Date!, $siteId:ID) {
                                              activitiesMaterials(from:$from,to:$to,siteId:$siteId) {
                                                id,type,name,precision,units,tracked,reorderDays,reorderBusinessDays
                                                used {quantity,onHand,onOrder,estimatedUsageToDate,estimatedCreated,activityId,activityDescription,startDate,endDate}
                                              }
                                            }`, {from:this.filters.dateStart, to:this.filters.dateEnd,siteId:this.filters.siteId});
      if (!results.error)
        this.materials = results.data.activitiesMaterials;
      this.loading = false;

    },
    async getRoles() {
      this.loading = true;
      const results = await this.$apiCall(`query PlanningScheduling($from:Date!, $to:Date!, $siteId:ID) {
                                              activitiesRoles(from:$from,to:$to,siteId:$siteId) {
                                                id,name,
                                                used {activityId,activityDescription,startDate,endDate}
                                              }
                                            }`, {from:this.filters.dateStart, to:this.filters.dateEnd,siteId:this.filters.siteId});
      if (!results.error)
        this.roles = results.data.activitiesRoles;
      this.loading = false;
    },
    async getPeople() {
      this.loading = true;
      const results = await this.$apiCall(`query PlanningScheduling($from:Date!, $to:Date!, $siteId:ID) {
                                              activitiesAssignTos(from:$from,to:$to,siteId:$siteId) {
                                                id,name,
                                                used {activityId,activityDescription,startDate,endDate}
                                              }
                                            }`, {from:this.filters.dateStart, to:this.filters.dateEnd,siteId:this.filters.siteId});
      if (!results.error)
        this.people = results.data.activitiesAssignTos;
      this.loading = false;
    },
    resourceInPeriod(resource, period) {
      if (!this.workDay(period.start))
        return null;
      const resources = resource.used.filter(activity => activity.startDate <= period.end && activity.endDate >= period.start);
      return resources.length ? resources : null;
    },
    equipmentConflicts(equipment, period) {
      for (let index = 0; index < equipment.used.length; index++) {
        const activity = equipment.used[index];
        if (period.start >= activity.startDate && period.start <= activity.endDate) {
          for (let i = index+1; i < equipment.used.length; i++) {
            const activityCompareto = equipment.used[i];
            if (period.start >= activityCompareto.startDate && period.start <= activityCompareto.endDate)
              return true;
          }
        }
      }

      return false;
    },
    async performDelete() {
      this.loading = true;
      const results = await this.$apiCall(`mutation PlanningScheduling($id:ID!) {
                                              objectDelete(id:$id)
                                            }`,
                                            {id:this.activity.id});
      if (!results.error) {
        this.getPeriod();
        this.showDelete = false;
      }
      this.loading = false;
    },
    async markAsComplete(activity) {
      this.loading = true;
      const results = await this.$apiCall(`mutation PlanningScheduling($id: ID!, $date:DateTime!) {
                                              activityComplete(id:$id, date:$date) {
                                                id
                                              }
                                            }`,
                                          {id:activity.id, date:format(new Date(),'yyyy-MM-dd HH:mm:ss')});
      if (!results.error)
        this.getPeriod();
      this.loading = false;
    },
    async markAsIncomplete(activity) {
      this.loading = true;
      const results = await this.$apiCall(`mutation PlanningScheduling($id: ID!) {
                                              activityMarkAsIncomplete(id:$id) {
                                                id
                                              }
                                            }`,
                                          {id:activity.id});
      if (!results.error)
        this.getPeriod();
      this.loading = false;
    },
    printWorkOrder(activity) {
      if (activity.primitiveType === 'Formula')
        this.printBlend(activity.primitiveId,
                       {date:activity.startDate, siteName:activity.siteName, quantity:activity.quantity, performBy:activity.roleName ?? ''},
                        true);
      else if (activity.primitiveType === 'MashBill')
        this.printMash(activity.primitiveId,
                       {date:activity.startDate, siteName:activity.siteName, quantity:activity.quantity, performBy:activity.roleName ?? ''},
                        true);
      else if (activity.primitiveType === 'Process')
        this.printDistillation(activity.primitiveId,
                       {date:activity.startDate, siteName:activity.siteName, quantity:activity.quantity, performBy:activity.roleName ?? ''},
                        true);
      else if (activity.primitiveType === 'Product')
        this.printBottling(activity.primitiveId,
                       {date:activity.startDate, siteName:activity.siteName, quantity:activity.quantity, performBy:activity.roleName ?? ''},
                        true);
    }
  }
}
</script>